import { Button, Carousel, Divider, Input, Form, List, message, Spin } from "antd";
import { AxiosError } from "axios";
import { OptiweCard } from "frontend-lib/Components/OptiweCard";
import { GlobalUser, User } from "frontend-lib/DomainTypes/GlobalUser";
import { Agent } from "frontend-lib/DomainTypes/User";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, serverUrl } from "Requests/Api";
import { useAppContext } from "State/Global/AppContext";
import FirebaseAuth from "./FirebaseAuth";
import { LoginOutlined } from "@ant-design/icons";
import "./Login.scss";
import { Logger } from "frontend-lib/Log";
import TermsAndCond from "frontend-lib/Utils/TermsAndCond";
import {
    parseCookiesGetSessionIdCookie,
    setSessionIdCookieLocalStorage,
} from "Requests/socketConfigs";

interface FormFields {
    email: string;
    password: string;
}

export default function Login() {
    const carousel = useRef();
    const [form] = Form.useForm();
    const [actualUser, setActualUser] = useState<Agent>();
    const [actualGlobalUser, setActualGlobalUser] = useState<GlobalUser>();
    const [isLoading, setIsLoading] = useState(false);
    const logger = new Logger("Agent/Login.tsx", serverUrl);

    // @ts-ignore
    const { actions } = useAppContext();
    const { t, i18n } = useTranslation();

    const userLangIsSpanish = navigator.language === "es" || navigator.language.includes("es-");

    useEffect(() => {
        if (userLangIsSpanish) {
            i18n.changeLanguage("es");
        } else {
            i18n.changeLanguage("en");
        }
    }, [i18n, userLangIsSpanish]);

    // @ts-ignore
    const handleNext = () => carousel.current.next();

    function parseAxiosError(err: AxiosError) {
        if (err.response?.status === 400) {
            message.error(`${t("noUserFound")}`);
            return;
        } else if (err.response?.status === 401) {
            message.error(`${t("login.errorDisabledWorkspace")}`);
            return;
        } else if (err.response?.status === 403) {
            message.error(`${t("login.errorDisabledWorkspace")}`);
            return;
        } else {
            message.error(t("serverError"));
            throw err;
        }
    }

    const setAppStateAuth = (workspaceName: string, actualUser: any, sessionId: string) => {
        actions.setWorkspaceName(workspaceName);
        actions.setAuth(true, actualUser);
        setSessionIdCookieLocalStorage(sessionId);
    };

    async function onFormFinish({ email, password }: FormFields) {
        setIsLoading(true);
        try {
            const actualUserRes = await api.auth().signIn({ email, password });
            setActualUser(actualUserRes.data);
            const globalUserRes = await api.globalUser().get(actualUserRes.data.id);
            const globalUser: GlobalUser = globalUserRes.data;
            setActualGlobalUser(globalUser);
            if (globalUser.users.length === 1) {
                const sessionId = parseCookiesGetSessionIdCookie(document.cookie);
                setAppStateAuth(globalUser.users[0].workspace.name, actualUserRes.data, sessionId!);
                return;
            }
            handleNext();
        } catch (err: any | AxiosError) {
            parseAxiosError(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function onAuthCb(accessToken: string) {
        try {
            const resUser = await api.auth().signInWithFirebase(accessToken);
            setActualUser(resUser.data);
            const globalUserRes = await api.globalUser().get(resUser.data.id);
            const globalUser: GlobalUser = globalUserRes.data;
            setActualGlobalUser(globalUser);
            if (globalUser.users.length === 1) {
                const sessionId = parseCookiesGetSessionIdCookie(document.cookie);
                setAppStateAuth(globalUser.users[0].workspace.name, resUser.data, sessionId!);
                return;
            }
            handleNext();
        } catch (err: any | AxiosError) {
            parseAxiosError(err);
        }
    }

    async function changeWorkspace(user: User) {
        const desiredWorkspaceId = user.workspace.id;
        const desiredUser = actualGlobalUser?.users.find(
            (u) => u.workspace.id === desiredWorkspaceId
        );

        if (user.workspace?.isDisabled) {
            message.error(`${t("login.errorDisabledWorkspace")}`);
            return;
        }

        if (desiredUser?.id === actualUser?.id) {
            const sessionId = parseCookiesGetSessionIdCookie(document.cookie);
            setAppStateAuth(desiredUser!.workspace.name, actualUser, sessionId!);
            return;
        }

        try {
            const resUser = await api.auth().changeWorkspace(desiredWorkspaceId);
            var agent = resUser.data;
            const workspaceName = actualGlobalUser?.users.find(
                (u) => u.workspace.id === desiredWorkspaceId
            )?.workspace.name;

            const sessionId = parseCookiesGetSessionIdCookie(document.cookie);
            setAppStateAuth(workspaceName!, agent, sessionId!);
            return;
        } catch (err: any | AxiosError) {
            parseAxiosError(err);
        }
    }

    function parseListItemClassName(user: User) {
        if (user.workspace?.isDisabled) {
            return "disabled-list-item-change-ws";
        }
        return "list-item-change-ws";
    }

    function parseLoginButtonClassName(user: User) {
        if (user.workspace?.isDisabled) {
            return "disabled-login-icon";
        }
        return "login-icon";
    }

    function renderLogin() {
        return (
            <>
                {isLoading ? (
                    <Spin
                        size="large"
                        style={{ display: "flex", justifyContent: "center", marginTop: "50%" }}
                    />
                ) : (
                    <>
                        <Form
                            form={form}
                            layout="vertical"
                            requiredMark="optional"
                            onFinish={onFormFinish}
                        >
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[{ required: true, message: t("required") }]}
                            >
                                <Input size="large" />
                            </Form.Item>
                            <Form.Item
                                label={t("password")}
                                name="password"
                                rules={[{ required: true, message: t("required") }]}
                            >
                                <Input type="password" size="large" />
                            </Form.Item>
                            <Button className="optButton" type="primary" htmlType="submit">
                                {t("signIn")}
                            </Button>
                        </Form>
                        <Divider style={{ color: "#B4B4B4" }}>or</Divider>
                        <FirebaseAuth onAuthCb={onAuthCb} />
                        <TermsAndCond />
                    </>
                )}
            </>
        );
    }

    function renderWorkspaceSelection() {
        return (
            <div>
                <h1>{t("selectWorkspace")}</h1>
                <List style={{ maxHeight: "30rem", overflow: "auto" }}>
                    {actualGlobalUser?.users.map((user) => (
                        <List.Item
                            className={parseListItemClassName(user)}
                            onClick={() => changeWorkspace(user)}
                            key={user.id}
                        >
                            <b>{user.workspace.name}</b>

                            <Button className={parseLoginButtonClassName(user)}>
                                <LoginOutlined />
                            </Button>
                        </List.Item>
                    ))}
                </List>
            </div>
        );
    }
    return (
        <div className="login" style={{ width: "auto" }}>
            <OptiweCard>
                {/* 
                // @ts-ignore */}
                <Carousel ref={carousel} dots={false}>
                    {renderLogin()}
                    {renderWorkspaceSelection()}
                </Carousel>
            </OptiweCard>
        </div>
    );
}
